import { extend } from "@dhx/ts-common/core";
import { el, VNode } from "@dhx/ts-common/dom";
import { List, MOVE_DOWN, MOVE_UP } from "@dhx/ts-list";

import { Id, IHandlers } from "@dhx/ts-common/types";
import { IDataViewConfig, IDataView } from "./types";
import { getEditor } from "./editors/editors";

export class DataView extends List implements IDataView {
	public config: IDataViewConfig;

	constructor(node: HTMLElement | string, config: IDataViewConfig = {}) {
		super(
			node,
			extend(
				{
					itemsInRow: 1,
					gap: "0px",
				},
				config
			)
		);
	}

	showItem(id: Id): void {
		const rootView = this.getRootView();
		if (!rootView || !rootView.node || !rootView.node.el || typeof id === "undefined") {
			return;
		}

		const dataViewEl: HTMLElement = this.getRootNode();
		if (!dataViewEl) {
			return;
		}

		const virtual = this.config.virtual;
		const index = this.data.getIndex(id);
		const currentRow = Math.floor(index / this.config.itemsInRow);
		const currentPage = Math.ceil(currentRow / dataViewEl.children.length) || 0;
		const itemRow = dataViewEl.children[
			currentRow - dataViewEl.children.length * currentPage
		] as HTMLElement;

		if (!virtual && !itemRow) return;
		const item = itemRow.children[index % this.config.itemsInRow] as HTMLElement;
		const gap = parseInt(this.config.gap.toString().replace("px", ""), null);

		if (item.offsetTop >= dataViewEl.clientHeight + dataViewEl.scrollTop - item.clientHeight) {
			dataViewEl.scrollTop = item.offsetTop - dataViewEl.clientHeight + item.clientHeight + gap;
		} else if (item.offsetTop < dataViewEl.scrollTop - gap) {
			dataViewEl.scrollTop = item.offsetTop - gap;
		}
	}

	protected _didRedraw(vm: any) {
		const rootEl = vm.node.el;
		const hasScroll = rootEl.scrollHeight > rootEl.offsetHeight;
		const classAttr: string = vm.node.attrs.class.replace(" dhx_dataview--has-scroll", "");
		const newClassName = hasScroll ? classAttr + " dhx_dataview--has-scroll" : classAttr;
		vm.node.patch({ class: newClassName });
	}

	protected _renderItem(item: any, index: number): VNode {
		const { itemsInRow, gap, template, itemHeight } = this.config;
		const html = template ? template(item) : item.htmlContent;
		const focus = item.id == this._focus;
		const isLastItemInRow = (index + 1) % this.config.itemsInRow === 0;

		const gapWithPx = (gapSize: any) => parseFloat(gapSize);
		if (item.id == this._edited) {
			const editor = getEditor(item, this);
			return editor.toHTML(isLastItemInRow);
		}
		const id = item.id.toString();

		const mx = this.data.getMetaMap(item);
		return el(
			"div",
			{
				class:
					"dhx_dataview-item" +
					(mx && mx.selected ? " dhx_dataview-item--selected" : "") +
					(focus ? " dhx_dataview-item--focus" : "") +
					(mx && mx.drop && !this._edited ? " dhx_dataview-item--drophere" : "") +
					(mx && mx.drag && !this._edited ? " dhx_dataview-item--dragtarget" : "") +
					(this.config.dragMode && !this._edited ? " dhx_dataview-item--drag" : "") +
					(gapWithPx(gap) ? " dhx_dataview-item--with-gap" : "") +
					(item.css ? " " + item.css : "") +
					(isLastItemInRow ? " dhx_dataview-item--last-item-in-row" : ""),
				style: {
					width: `calc(${100 / itemsInRow}% - ${gapWithPx(gap)} * ${(itemsInRow - 1) /
						itemsInRow}px)`,
					"margin-right": isLastItemInRow ? "" : gap,
					height: template ? null : itemHeight,
				},
				_key: id,
				"data-dhx-id": id,
				_ref: id,
				...this.getDataViewItemAriaAttrs(this, item),
				tabindex: focus ? 0 : -1,
			},
			html
				? [
						el(".dhx_dataview-item__inner-html", {
							".innerHTML": html,
							role: "presentation",
						}),
				  ]
				: item.value || item.text || item.value
		);
	}

	protected _renderList(): VNode {
		const data = this.data.getRawData(0, -1);

		const { itemsInRow, css, gap } = this.config;
		let currentCounter = 0;
		const rows = data.reduce((items, obj, index) => {
			if (currentCounter === 0) {
				items.push([]);
			}
			items[items.length - 1].push(this._renderItem(obj, index));
			currentCounter = (currentCounter + 1) % itemsInRow;
			return items;
		}, []);
		return el(
			"",
			{
				...this._handlers,
				"data-dhx-widget-id": this._uid,
				class:
					(css ? css : "") +
					" dhx_widget dhx_dataview" +
					(this.config.multiselection && this.selection.getItem() ? " dhx_no-select--pointer" : ""),
				style: {
					height: this.config.height,
				},
				...this.getDataViewAriaAttrs(this.config, this.data.getLength(), rows.length, itemsInRow),
				tabindex: 0,
			},
			rows.map((row, index) =>
				el(
					".dhx_dataview-row",
					{
						style: { margin: gap },
						"aria-label": `Row ${index + 1}`,
					},
					row
				)
			)
		);
	}

	protected _getHotkeys(): IHandlers {
		const handlers = super._getHotkeys();
		handlers.arrowUp = e => {
			this.moveFocus(MOVE_UP, this.config.itemsInRow);
			if (this._changed) this._changed = false;
			e.preventDefault();
		};
		handlers.arrowDown = e => {
			this.moveFocus(MOVE_DOWN, this.config.itemsInRow);
			if (this._changed) this._changed = false;
			e.preventDefault();
		};
		handlers.arrowLeft = e => {
			this.moveFocus(MOVE_UP);
			if (this._changed) this._changed = false;
			e.preventDefault();
		};
		handlers.arrowRight = e => {
			this.moveFocus(MOVE_DOWN);
			if (this._changed) this._changed = false;
			e.preventDefault();
		};

		return handlers;
	}

	protected getDataViewItemAriaAttrs(context: this, item: any) {
		const getAriaGrabbed = (context, item) =>
			context.config.dragMode && !context._edited
				? { "aria-grabbed": Boolean(item.$dragtarget && !context._edited).toString() }
				: {};

		const getAriaRoleDescription = context =>
			context.config.editable
				? {
						"aria-roledescription": `${
							context._edited ? "Press Enter to stop editing" : "Double click to edit content"
						}`,
				  }
				: {};

		return {
			role: "option",
			"aria-selected": item.$selected ? "true" : "false",
			...getAriaGrabbed(context, item),
			...getAriaRoleDescription(context),
		};
	}

	protected getDataViewAriaAttrs(config, itemsCount, rowsCount, itemsInRow) {
		return {
			role: "listbox",
			"aria-label": `Dataview, ${itemsCount} options on ${rowsCount} rows, ${itemsInRow} options per row.${
				config.editable ? " Content is editable." : ""
			}`,
			"aria-multiselectable": config.selection && config.multiselection ? "true" : "false",
			"aria-readonly": config.editable ? "false" : "true",
		};
	}
}
